// WHAT: Hook for generating daily plan from shortfalls with intelligent fallback logic
// WHY:  Provides daily work plan based on shortfalls with automatic task matching
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: core.invoke from Tauri API
// RISKS:
//   - NOTE: Plan generation depends on backend - may fail silently
//   - NOTE: Intelligent shortfall selection logic is complex - may have edge cases
//   - NOTE: Task matching depends on backend algorithm - may not match user expectations
//   - NOTE: Daily capacity calculation depends on config structure

import type { SupabaseTask } from "./data/useSupabasetasks";

import { core } from "@tauri-apps/api";
import { useEffect, useState } from "react";

/**
 * Chunk entry interface
 */
export interface ChunkEntry {
  /** Aspect name */
  child: string;
  /** Workspace name */
  parent: string;
  /** Shortfall before this chunk */
  shortfall_before: number;
  /** Hours to use */
  use_hours: number;
}

/**
 * Matched chunk task interface
 */
export interface MatchedChunkTask {
  /** Aspect name */
  child: string;
  /** Task deadline */
  deadline: null | string;
  /** Workspace name */
  parent: string;
  /** Task score */
  score: null | number;
  /** Shortfall before this chunk */
  shortfall_before: number;
  /** Matched task ID */
  task_id: null | string;
  /** Matched task name */
  task_name: null | string;
  /** Hours to use */
  use_hours: number;
}

/**
 * Shortfall entry interface
 */
export interface ShortfallEntry {
  /** Aspect name */
  child: string;
  /** Workspace name */
  parent: string;
  /** Planned hours monthly */
  planned: number;
  /** Planned hours until today */
  plannedUntilToday: number;
  /** Shortfall for full month */
  shortfallFullMonth: number;
  /** Shortfall until today */
  shortfallUntilToday: number;
  /** Entry status */
  status: string;
  /** Total hours worked */
  worked: number;
}

/**
 * WHAT:
 *   Hook that generates daily plan from shortfalls with intelligent fallback
 *   logic and task matching.
 *
 * WHY:
 *   Enables automatic daily work planning based on shortfalls with smart
 *   selection between daily and monthly shortfalls.
 *
 * BEHAVIOR:
 *   - Calculates daily capacity from config (weekday/weekend)
 *   - Uses external worked hours if provided
 *   - Intelligently selects shortfall source (daily vs monthly)
 *   - Generates plan chunks via backend
 *   - Matches chunks with tasks
 *   - Returns daily plan with capacity and source info
 *
 * RISKS:
 *   - NOTE: Plan generation depends on backend - may fail silently
 *   - NOTE: Intelligent shortfall selection logic is complex - may have edge cases
 *   - NOTE: Task matching depends on backend algorithm - may not match user expectations
 *   - NOTE: Daily capacity calculation depends on config structure
 *
 * @param root0
 *
 * @param root0.shortfalls
 *
 * @param root0.shortfallViewMode
 *
 * @param root0.tasks
 *
 * @param root0.config
 *
 * @param root0.workedToday
 *
 * @returns {{ dailyPlan: MatchedChunkTask[], dailyCapacity: number, workedToday: number, shortfallSource: string }} Daily plan data with capacity and source info
 *
 */
export function useDailyPlan({
  config,
  shortfalls,
  shortfallViewMode,
  tasks,
  workedToday: externalWorkedToday = 0, // NOTE: Add optional parameter for worked hours
}: {
  shortfalls: ShortfallEntry[];
  shortfallViewMode: "full" | "today";
  tasks: SupabaseTask[];
  config: Record<string, Record<string, { color: string; hours: number }>>;
  workedToday?: number; // NOTE: Add optional parameter
}) {
  const [dailyPlan, setDailyPlan] = useState<MatchedChunkTask[]>([]);
  const [dailyCapacity, setDailyCapacity] = useState<number>(0);
  const [workedToday, setWorkedToday] = useState<number>(externalWorkedToday);
  const [shortfallSource, setShortfallSource] = useState<string>(shortfallViewMode);

  useEffect(() => {
    const generatePlan = async () => {
      if (tasks.length === 0 || shortfalls.length === 0) {
        return;
      }

      try {
        // NOTE: Calculate daily capacity from config
        // Settings are filtered out by filterWorkspaceConfig, so we use defaults
        const isWeekend = [0, 6].includes(new Date().getDay());
        const calculatedDailyCapacity = isWeekend ? 8 : 5;

        setDailyCapacity(calculatedDailyCapacity);

        // NOTE: Use external worked hours if provided, otherwise calculate from shortfalls
        const workedTodayHours = externalWorkedToday;
        if (workedTodayHours === 0) {
          // NOTE: If no external worked hours provided, we can't calculate from shortfalls
          // as they contain monthly data, not daily data
        }

        setWorkedToday(workedTodayHours);

        // NOTE: Intelligent shortfall selection: Use monthly shortfalls if daily shortfalls are all negative
        const dailyShortfalls = shortfalls.map((entry) => ({
          child: entry.child,
          parent: entry.parent,
          shortfall: entry.shortfallUntilToday,
          status: entry.status,
        }));

        const monthlyShortfalls = shortfalls.map((entry) => ({
          child: entry.child,
          parent: entry.parent,
          shortfall: entry.shortfallFullMonth,
          status: entry.status,
        }));

        // NOTE: Check if all daily shortfalls are zero or negative (met or overfulfilled)
        // Use a small threshold to account for floating point precision
        const threshold = 0.001; // 0.001 hours = 3.6 seconds
        const dailyZeroOrNegativeCount = dailyShortfalls.filter(
          (s) => s.shortfall <= threshold,
        ).length;
        const monthlyPositiveCount = monthlyShortfalls.filter(
          (s) => s.shortfall > threshold,
        ).length;
        const allDailyZeroOrNegative = dailyZeroOrNegativeCount === dailyShortfalls.length;
        const hasPositiveMonthly = monthlyPositiveCount > 0;

        let selectedShortfalls;
        let usedViewMode;

        if (allDailyZeroOrNegative && hasPositiveMonthly) {
          // NOTE: Use monthly shortfalls if daily are all zero/negative but monthly has positive ones
          selectedShortfalls = monthlyShortfalls;
          usedViewMode = "monthly (fallback)";
        } else {
          // NOTE: Use the originally selected view mode
          selectedShortfalls = shortfallViewMode === "today" ? dailyShortfalls : monthlyShortfalls;
          usedViewMode = shortfallViewMode;
        }

        setShortfallSource(usedViewMode);

        const [rawChunks] = await core.invoke<[ChunkEntry[], number]>("plan_for_today_chunks", {
          chunks: selectedShortfalls,
          isweekend: isWeekend,
          workedToday: workedTodayHours,
        });

        if (rawChunks.length === 0) {
          setDailyPlan([]);
          return;
        }

        const matched = await core.invoke<MatchedChunkTask[]>("get_matched_chunk_tasks", {
          chunks: rawChunks,
          tasks,
        });

        setDailyPlan(matched);
      } catch {
        // NOTE: Failed to calculate daily plan - silently ignore to prevent UI disruption
      }
    };

    void generatePlan();
  }, [shortfalls, shortfallViewMode, tasks, externalWorkedToday, config]);

  return { dailyCapacity, dailyPlan, shortfallSource, workedToday };
}
